import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, api, errorMessage } from "@/lib/api";

const Rule = ({ style }) => {
  if (style === "minimal") return <div className="mt-4 h-px w-full bg-slate-200" />;
  return (
    <div className="mt-4 space-y-[2px]">
      <div className="h-[1.5px] w-full bg-slate-900" />
      <div className="h-[0.5px] w-full bg-slate-900/60" />
    </div>
  );
};

export const LetterheadPreview = ({ form }) => {
  const [busy, setBusy] = useState(false);
  const style = form.template_style || "classic";
  const contactBits = [form.address, form.phone, form.email, form.website]
    .filter(Boolean)
    .map((b) => String(b).replace(/\n/g, ", "));
  const ids = [
    form.tax_id ? `${form.tax_label || "GST"} No.: ${form.tax_id}` : "",
    form.bar_council_id ? `Enrolment: ${form.bar_council_id}` : "",
  ].filter(Boolean);

  const openSample = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/settings/preview.pdf",
        {
          ...form,
          default_tax_rate: Number(form.default_tax_rate || 0),
          payment_terms_days: Number(form.payment_terms_days || 0),
          next_number: Number(form.next_number || 1),
          number_padding: Number(form.number_padding || 4),
        },
        { responseType: "blob" },
      );
      window.open(URL.createObjectURL(data), "_blank", "noopener");
    } catch (error) {
      toast.error(errorMessage(error, "Could not build the sample"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="surface p-6" data-testid="settings-letterhead-preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-slate-900">Letterhead preview</h2>
          <p className="mt-1.5 text-[12px] text-slate-500">
            Updates as you type. This is the head and sign-off that prints on every fee note,
            receipt and statement.
          </p>
        </div>
        <button
          type="button"
          onClick={openSample}
          disabled={busy}
          data-testid="letterhead-sample-button"
          className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
          )}
          Sample invoice PDF
        </button>
      </div>

      <div className="mt-6 border border-slate-200 bg-white p-6 shadow-[0_1px_0_rgba(51,31,38,0.04)] sm:p-8">
        <div
          className={
            style === "modern"
              ? "flex items-center gap-5 border-l-[3px] border-slate-900 bg-slate-50 p-4"
              : "flex items-center gap-5"
          }
        >
          {form.logo_path ? (
            <img
              src={`${API}/files/${form.logo_path}`}
              alt="Chambers crest"
              className="h-16 w-16 shrink-0 object-contain"
              data-testid="letterhead-preview-crest"
            />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center border border-dashed border-slate-300 text-[9px] uppercase tracking-[0.1em] text-slate-400">
              No crest
            </div>
          )}
          <div className="min-w-0">
            <p className="font-serif text-xl leading-tight text-slate-900">
              {form.counsel_name || "Counsel name"}
            </p>
            {form.designation && (
              <p className="mt-1 text-[11px] text-slate-600">{form.designation}</p>
            )}
            {form.chambers_name && (
              <p className="text-[11px] text-slate-600">{form.chambers_name}</p>
            )}
            {contactBits.length > 0 && (
              <p className="mt-1 text-[10px] leading-relaxed text-slate-500">
                {contactBits.join("  |  ")}
              </p>
            )}
            {ids.length > 0 && (
              <p className="num text-[10px] text-slate-500">{ids.join("  |  ")}</p>
            )}
          </div>
        </div>

        {style !== "modern" && <Rule style={style} />}

        <div className="mt-6 flex items-start justify-between gap-6">
          <div>
            <p className="label-caps">Billed to</p>
            <p className="mt-1.5 text-[12px] font-semibold text-slate-900">
              {form.default_party_display === "client"
                ? "Represented party (client)"
                : "Instructing lawyer / firm"}
            </p>
            {form.default_party_display === "both" && (
              <p className="text-[11px] text-slate-500">
                with the represented party shown in the matter block
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="font-serif text-lg text-slate-900">INVOICE</p>
            <p className="num text-[11px] text-slate-500">
              {`${form.invoice_prefix || "INV-"}${String(form.next_number || 1).padStart(
                Number(form.number_padding || 4),
                "0",
              )}`}
            </p>
          </div>
        </div>

        <div className="mt-6 border-t border-slate-100 pt-5">
          <div className="flex items-end justify-between gap-6">
            <p className="text-[11px] text-slate-500">
              {form.tax_enabled === false
                ? `No ${form.tax_label || "GST"} is added to fee notes`
                : `${form.tax_label || "GST"} at ${Number(form.default_tax_rate || 0)}% by default`}
            </p>
            <div className="text-right">
              {form.signature_path ? (
                <img
                  src={`${API}/files/${form.signature_path}`}
                  alt="Signature"
                  className="ml-auto h-12 object-contain"
                  data-testid="letterhead-preview-signature"
                />
              ) : (
                <div className="ml-auto h-12 w-32 border-b border-dashed border-slate-300" />
              )}
              <p className="mt-1.5 font-serif text-[13px] text-slate-900">
                {form.counsel_name || "Counsel name"}
              </p>
              <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500">
                {form.designation || "Advocate"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
